import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Minus } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmReset, OpsNote, OpsSwitch, PresetBar, RolePicker } from "./PermissionBits";

const ROLE = { owner: "Titolare", manager: "Manager", editor: "Editor", viewer: "Lettore" };
const OTHERS = ["manager", "editor", "viewer"];

function Cell({ p, r, draft, edit, onToggle, mob }) {
  const on = r === "owner" || draft[p.perm]?.includes(r);
  const vis = r === mob ? "" : "hidden sm:table-cell";
  if (r === "owner" || !edit || !p.customizable) return <td className={`px-2 py-1.5 text-center ${vis}`} data-testid={`perm-${p.perm}-${r}`}>
    {(r === "owner" ? true : p.roles[r]) ? <Check size={14} className="mx-auto text-emerald-600" aria-label="sì" /> : <Minus size={14} className="mx-auto text-slate-300" aria-label="no" />}</td>;
  return <td className={`px-2 py-1.5 text-center ${vis}`}><Checkbox checked={on} onCheckedChange={() => onToggle(p.perm, r)} aria-label={`${p.label}: ${ROLE[r]}`} className="mx-auto h-5 w-5 sm:h-4 sm:w-4" data-testid={`perm-${p.perm}-${r}`} /></td>;
}

// The owner ticks, per single action, what managers, editors and viewers may do. The server enforces exactly this table.
export function PermissionMatrix({ tid, onPermsChanged }) {
  const api = useApi();
  const [m, setM] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [mob, setMob] = useState("manager");
  const load = useCallback(() => api.get(`/t/${tid}/permissions`).then(({ data }) => {
    setM(data); setDraft(Object.fromEntries(data.groups.flatMap((g) => g.perms).map((p) => [p.perm, Object.keys(p.granted).filter((r) => p.granted[r])])));
  }).catch(() => setM(null)), [api, tid]);
  useEffect(() => { load(); }, [load]);
  const all = useMemo(() => (m ? m.groups.flatMap((g) => g.perms) : []), [m]);
  const dirty = all.filter((p) => p.customizable && OTHERS.some((r) => p.granted[r] !== draft[p.perm]?.includes(r)));
  if (!m) return null;
  const edit = m.can_edit;
  const toggle = (perm, r) => setDraft((d) => ({ ...d, [perm]: d[perm].includes(r) ? d[perm].filter((x) => x !== r) : [...d[perm], r] }));
  const send = async (body, ok) => {
    setBusy(true);
    try { await api.put(`/t/${tid}/permissions`, body); toast.success(ok); await load(); onPermsChanged?.(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const save = () => send({ perms: Object.fromEntries(dirty.map((p) => [p.perm, draft[p.perm]])) }, "Permessi salvati");
  return (
    <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="permission-matrix">
      <div><p className="text-sm font-medium text-slate-800">Cosa può fare ogni ruolo</p>
        <p className="text-xs text-slate-500">{edit ? "Spunta le azioni consentite a ogni ruolo. Il titolare può sempre tutto; gestire il team e questa tabella resta solo al titolare." : "Le regole le decide il titolare. Questa tabella mostra cosa è consentito oggi."}</p></div>
      {edit ? <OpsSwitch on={m.ops_self_service} busy={busy} support={m.my_role === "support"} onChange={(v) => send({ ops_self_service: v }, v ? "Operazioni attive nell'area cliente" : "Operazioni di nuovo gestite da MUSORA")} />
        : !m.ops_self_service && <OpsNote />}
      {edit && <PresetBar presets={m.presets} busy={busy} onApply={(preset, role) => send({ preset, role }, "Profilo applicato: ora puoi ritoccare le singole spunte")} />}
      <RolePicker roles={m.roles} value={mob} onChange={setMob} />
      <div className="sm:overflow-x-auto">
        <table className="w-full text-xs sm:min-w-[560px]">
          <thead><tr className="text-left text-slate-500"><th className="bg-white px-2 py-1.5 font-medium sm:sticky sm:left-0">Azione</th>
            {m.roles.map((r) => <th key={r} className={`px-2 py-1.5 text-center font-medium ${r === m.my_role ? "text-sky-700" : ""} ${r === mob ? "" : "hidden sm:table-cell"}`}>{ROLE[r]}{r === m.my_role ? " (tu)" : ""}</th>)}</tr></thead>
          {m.groups.map((g) => (
            <tbody key={g.name}>
              <tr><th colSpan={m.roles.length + 1} className="bg-slate-50 px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">{g.name}</th></tr>
              {g.perms.map((p) => (
                <tr key={p.perm} className="border-t border-slate-100" data-testid={`perm-row-${p.perm}`}>
                  <td className="bg-white px-2 py-1.5 text-slate-700 sm:sticky sm:left-0">{p.label}
                    {p.adm_only && <span className="block text-[11px] text-amber-700">{edit ? "Vale quando attivi le operazioni qui sopra" : "Per ora lo fa MUSORA"}</span>}
                    {p.customized && <span className="block text-[11px] text-sky-700" data-testid={`perm-customized-${p.perm}`}>Modificato rispetto al predefinito</span>}</td>
                  {m.roles.map((r) => <Cell key={r} p={p} r={r} draft={draft} edit={edit} onToggle={toggle} mob={mob} />)}
                </tr>))}
            </tbody>))}
        </table>
      </div>
      {edit && <div className="flex flex-wrap gap-2">
        <Button className="h-10" disabled={busy || !dirty.length} onClick={save} data-testid="perm-save">{busy && <Loader2 size={15} className="mr-1.5 animate-spin" />}Salva modifiche{dirty.length ? ` (${dirty.length})` : ""}</Button>
        {dirty.length > 0 && <Button variant="outline" className="h-10" disabled={busy} onClick={load} data-testid="perm-discard">Annulla modifiche</Button>}
        <ConfirmReset disabled={busy || !m.customized_count} onConfirm={() => send({ reset_all: true }, "Permessi predefiniti ripristinati")} />
      </div>}
    </section>);
}
